import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, Col, DatePicker, Drawer, Empty, Input, Pagination, Row, Select, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { FileExcelOutlined, InfoCircleOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import { useToast } from '@/components/ui/use-toast';
import { spuAmountApi, type GroupBy, type SpuAmountFilterItem, type SpuAmountReportRow } from '@/services/api/spuAmountDisplay';
import { downloadXlsx, type XlsxCellFormat } from '@/lib/simpleXlsx';
import {
  buildBrandOptions,
  buildCategoryOptions,
  buildSpuOptions,
  displayFilterValue,
  getDisplaySpu,
  sanitizeSelectedValues,
} from './filterOptions';
import { getBudgetAbnormalInfoFromPeak } from './budgetAbnormal';
import { buildRollingMonthColumns, getVisibleSummaryKeys, type MonthViewMode, type RollingMonthKey } from './monthWindow';

const { Title, Text } = Typography;
const { Option } = Select;
const SKU_COLLAPSIBLE_DIM_KEYS = new Set(['brand', 'category', 'spu']);
const SKU_HIDDEN_METRIC_SORT_NOS = new Set([6, 7]);

const productAttrOptions = [
  { label: '主品', value: '主品' },
  { label: '赠品PWP', value: '赠品PWP' },
  { label: '赠品GWP', value: '赠品GWP' },
  { label: '无', value: '__EMPTY__' },
];

dayjs.locale('zh-cn');

type MetricKey = number; // 使用 sort_no 作为指标标识
type ValueFormat = 'money' | 'ratio' | 'number' | 'percent' | 'moneyDelta' | 'abnormalFlag';
type BudgetAbnormalFilter = 'all' | 'abnormal' | 'normal';
type SkuAbnormalFilter = 'all' | 'abnormal' | 'normal';

type DisplayRow = {
  key: string;
  groupKey: string;
  rowSpan: number;
  metricKey: MetricKey;
  metricLabel: string;
  format: ValueFormat;
  brand: string;
  spu: string;
  sku: string;
  skuName: string;
  goodsProperty: string;
  category: string;
  isAbnormal: boolean;
  abnormalReason: string;
  skuAbnormalFlag: number | null;
  m1: number | null;
  m2: number | null;
  m3: number | null;
  m4: number | null;
  m5: number | null;
  m6: number | null;
  m7: number | null;
  m8: number | null;
  m9: number | null;
  m10: number | null;
  m11: number | null;
  m12: number | null;
  w1: number | null;
  w2: number | null;
  w3: number | null;
  w4: number | null;
  w5: number | null;
  w6: number | null;
  w7: number | null;
  ttl: number | null;
  ytd: number | null;
  ytg: number | null;
  q1: number | null;
  q2: number | null;
  q3: number | null;
  q4: number | null;
  h1: number | null;
  h2: number | null;
  ttlShare: number | null;
  ytdShare: number | null;
  ytgShare: number | null;
  q1Share: number | null;
  q2Share: number | null;
  q3Share: number | null;
  q4Share: number | null;
};

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);
const monthKey = (month: number) => `m${month}` as const;
const ROLLING_MONTH_KEYS: RollingMonthKey[] = ['w1', 'w2', 'w3', 'w4', 'w5', 'w6', 'w7'];

// 根据 sort_no 确定指标格式（需要根据实际接口数据调整）
const metricFormatBySortNo: Record<number, ValueFormat> = {
  1: 'money',    // 2025实际
  2: 'number',   // 2025倍率（爆发系数）- 显示比值，保留两位小数
  3: 'money',    // 2026FCST
  4: 'percent',  // 2026EVOL
  5: 'number',   // 2026倍率（爆发系数）- 显示比值，保留两位小数
  6: 'money',    // 2026预算(TM)
  7: 'moneyDelta', // 差异
  // 其他 sort_no 的格式根据实际数据添加
};

// 根据 sort_no 判断是否需要计算汇总值（ttl, ytd, ytg, q1-q4, h1, h2）
const SUMMARY_METRIC_SORT_NOS = new Set([1, 3, 6]); // actual2025, fcst2026, budget2026

// 关键指标的 sort_no（用于样式和渲染逻辑）
const FCST_SORT_NO = 3;      // 2026FCST
const DIFF_SORT_NO = 7;      // 差异
const BUDGET_SORT_NO = 6;    // 2026预算(TM)

const periodIndices = {
  ttl: MONTHS.map((month) => month - 1),
  q1: [0, 1, 2],
  q2: [3, 4, 5],
  q3: [6, 7, 8],
  q4: [9, 10, 11],
  h1: [0, 1, 2, 3, 4, 5],
  h2: [6, 7, 8, 9, 10, 11],
} as const;

const formatters = {
  money0: new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 }),
  ratio2: new Intl.NumberFormat('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  percent0: new Intl.NumberFormat('zh-CN', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 0 }),
};

const renderValue = (value: number | null, fmt: ValueFormat | XlsxCellFormat | 'text') => {
  if (fmt === 'text') return value ?? '';
  if (value === null || Number.isNaN(value)) return '';
  switch (fmt) {
    case 'money':
      return formatters.money0.format(value);
    case 'ratio':
      return formatters.ratio2.format(value * 100);
    case 'number':
      return formatters.ratio2.format(value);
    case 'percent':
      return formatters.percent0.format(value);
    case 'moneyDelta':
      return `${value > 0 ? '+' : ''}${formatters.money0.format(value)}`;
    case 'abnormalFlag':
      return value === 1 ? '异常' : '正常';
    default:
      return value;
  }
};

const sumNullable = (arr: Array<number | null>) => arr.reduce<number>((acc, value) => acc + (value ?? 0), 0);

const sumByIndices = (arr: Array<number | null>, idxList: readonly number[]) =>
  idxList.reduce<number>((acc, idx) => acc + (arr[idx] ?? 0), 0);

const ratioByIndices = (numerator: Array<number | null>, denominator: Array<number | null>, idxList: readonly number[]) => {
  const denominatorTotal = sumByIndices(denominator, idxList);
  if (!denominatorTotal) return null;
  return sumByIndices(numerator, idxList) / denominatorTotal - 1;
};

const diffRateSeries = (numerator: Array<number | null>, denominator: Array<number | null>) =>
  MONTHS.map((_, index) => {
    const denominatorValue = denominator[index] ?? 0;
    if (!denominatorValue) return null;
    return (numerator[index] ?? 0) / denominatorValue - 1;
  });

const diffSeries = (numerator: Array<number | null>, denominator: Array<number | null>) =>
  numerator.map((value, index) => {
    const denominatorValue = denominator[index] ?? 0;
    if (!denominatorValue) return null;
    return (value ?? 0) - denominatorValue;
  });

const aggregateMomSeries = (
  rows: SpuAmountReportRow[],
  valueRows: SpuAmountReportRow[],
  getSeries: (row?: SpuAmountReportRow) => Array<number | null>,
) =>
  MONTHS.map((_, index) => {
    let numeratorTotal = 0;
    let denominatorTotal = 0;

    valueRows.forEach((valueRow) => {
      const numerator = getSeries(valueRow)[index] ?? 0;
      numeratorTotal += numerator;

      const ratioRow = rows.find((row) => row.group_id === valueRow.group_id);
      const ratio = getSeries(ratioRow)[index];
      if (ratio) {
        denominatorTotal += numerator / ratio;
        return;
      }

      if (index > 0) {
        denominatorTotal += getSeries(valueRow)[index - 1] ?? 0;
      }
    });

    if (!denominatorTotal) return null;
    return numeratorTotal / denominatorTotal;
  });

const reactNodeToText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactNodeToText).join('');
  if (React.isValidElement(node)) return reactNodeToText((node.props as { children?: React.ReactNode }).children);
  return '';
};

const getColumnExportTitle = (col: any) => {
  if (col.exportTitle !== undefined && col.exportTitle !== null) return String(col.exportTitle);
  const titleText = reactNodeToText(col.title).trim();
  if (titleText) return titleText;
  return String(col.dataIndex ?? col.key ?? '');
};

const flattenColumns = (cols: any[]): Array<{ title: string; key: string; dataIndex?: string; exportFmt: XlsxCellFormat | 'text' }> => {
  const output: Array<{ title: string; key: string; dataIndex?: string; exportFmt: XlsxCellFormat | 'text' }> = [];
  const walk = (list: any[], prefix?: string) => {
    list.forEach((col) => {
      const rawTitle = getColumnExportTitle(col);
      const title = prefix ? `${prefix}-${rawTitle}` : rawTitle;
      if (col.children?.length) {
        walk(col.children, title);
        return;
      }
      output.push({
        title,
        key: String(col.key ?? col.dataIndex ?? col.title),
        dataIndex: col.dataIndex,
        exportFmt: col.exportFmt ?? 'text',
      });
    });
  };
  walk(cols);
  return output;
};

const getMonthSeries = (row?: SpuAmountReportRow) =>
  MONTHS.map((month) => row?.[`m${String(month).padStart(2, '0')}` as keyof SpuAmountReportRow] as number | null ?? null);

const getRollingMonthSeries = (row?: SpuAmountReportRow) =>
  ROLLING_MONTH_KEYS.map((key) => row?.[key] ?? null);

const getBizMonthIndices = (bizMonth: Dayjs) => {
  const monthNumber = bizMonth.month() + 1;
  return {
    ytd: Array.from({ length: monthNumber }, (_, idx) => idx),
    ytg: Array.from({ length: Math.max(0, 12 - monthNumber) }, (_, idx) => monthNumber + idx),
  };
};

const getFcstWindowIndices = (bizMonth: Dayjs) => {
  const startIndex = bizMonth.month();
  const endExclusive = Math.min(startIndex + 6, MONTHS.length);
  return Array.from({ length: endExclusive - startIndex }, (_, idx) => startIndex + idx);
};

const getDefaultBizMonth = (currentDate: Dayjs = dayjs()) => {
  return currentDate.startOf('month');
};

const getAbnormalInfo = (diffRateMonths: Array<number | null>, bizMonth: Dayjs) => {
  const activeDiffs = getFcstWindowIndices(bizMonth)
    .map((index) => diffRateMonths[index])
    .filter((value): value is number => value !== null);
  if (!activeDiffs.length) {
    return { isAbnormal: false, abnormalReason: '' };
  }

  const peak = activeDiffs.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
  if (peak <= 0.25) {
    return { isAbnormal: false, abnormalReason: '' };
  }

  return {
    isAbnormal: true,
    abnormalReason: `前6个月预算偏差峰值 ${(peak * 100).toFixed(0)}%`,
  };
};

const getRollingAbnormalInfo = (diffRateMonths: Array<number | null>) => {
  const activeDiffs = diffRateMonths.slice(1).filter((value): value is number => value !== null);
  if (!activeDiffs.length) {
    return { isAbnormal: false, abnormalReason: '' };
  }

  const peak = activeDiffs.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
  if (peak <= 0.25) {
    return { isAbnormal: false, abnormalReason: '' };
  }

  return {
    isAbnormal: true,
    abnormalReason: `未来6个月预算偏差峰值 ${(peak * 100).toFixed(0)}%`,
  };
};

const buildDisplayRows = (
  rawRows: SpuAmountReportRow[],
  bizMonth: Dayjs,
  monthViewMode: MonthViewMode,
  budgetAbnormalFilter: BudgetAbnormalFilter,
  skuAbnormalFilter: SkuAbnormalFilter = 'all',
  groupBy: GroupBy = 'spu',
  visibleMetrics?: Set<MetricKey>,
) => {
  const groupedRows = new Map<string, SpuAmountReportRow[]>();
  rawRows.forEach((row) => {
    const current = groupedRows.get(row.group_id) ?? [];
    current.push(row);
    groupedRows.set(row.group_id, current);
  });

  const { ytd, ytg } = getBizMonthIndices(bizMonth);

  return Array.from(groupedRows.values())
    .sort((a, b) => (a[0]?.rn ?? 0) - (b[0]?.rn ?? 0))
    .flatMap((groupRows) => {
      const sortedRows = [...groupRows].sort((a, b) => a.sort_no - b.sort_no);
      const rowMap = new Map(sortedRows.map((row) => [row.sort_no, row]));
      const getSeries = monthViewMode === 'rolling' ? getRollingMonthSeries : getMonthSeries;
      const actualMonths = getSeries(rowMap.get(1));
      const fcstMonths = getSeries(rowMap.get(3));
      const budgetMonths = getSeries(rowMap.get(6));
      const backendDiffMonths = getSeries(rowMap.get(7));
      const varianceRateMonths = diffRateSeries(fcstMonths, budgetMonths);
      const backendBudgetPeakAbsDiffRate = sortedRows.find((row) => row.budget_peak_abs_diff_rate !== null && row.budget_peak_abs_diff_rate !== undefined)?.budget_peak_abs_diff_rate;
      const { isAbnormal, abnormalReason } =
        groupBy === 'sku'
          ? getBudgetAbnormalInfoFromPeak(backendBudgetPeakAbsDiffRate)
          : monthViewMode === 'rolling'
            ? getRollingAbnormalInfo(varianceRateMonths)
            : getAbnormalInfo(varianceRateMonths, bizMonth);
      const hasSkuAbnormal = sortedRows.some((row) => Number(row.sku_abnormal_flag) === 1);

      if (budgetAbnormalFilter === 'abnormal' && !isAbnormal) {
        return [];
      }
      if (budgetAbnormalFilter === 'normal' && isAbnormal) {
        return [];
      }
      if (skuAbnormalFilter === 'abnormal' && !hasSkuAbnormal) {
        return [];
      }
      if (skuAbnormalFilter === 'normal' && hasSkuAbnormal) {
        return [];
      }

      let visibleRows = groupBy === 'sku' ? sortedRows.filter((row) => !SKU_HIDDEN_METRIC_SORT_NOS.has(row.sort_no)) : sortedRows;
      if (visibleMetrics) {
        visibleRows = visibleRows.filter((row) => visibleMetrics.has(row.sort_no));
      }

      return visibleRows.map((row, index) => {
        const sortNo = row.sort_no;
        let months = getSeries(row);
        const format = metricFormatBySortNo[sortNo] ?? 'number';

        let ttl = row.ttl_amt ?? null;
        let ytdValue = row.ytd_amt ?? null;
        let ytgValue: number | null = null;
        let q1: number | null = null;
        let q2: number | null = null;
        let q3: number | null = null;
        let q4: number | null = null;
        let h1: number | null = null;
        let h2: number | null = null;

        if (monthViewMode === 'rolling') {
          ttl = null;
          ytdValue = null;
          ytgValue = null;
        } else if (SUMMARY_METRIC_SORT_NOS.has(sortNo)) {
          ttl = row.ttl_amt ?? sumNullable(months);
          ytdValue = row.ytd_amt ?? sumByIndices(months, ytd);
          ytgValue = sumByIndices(months, ytg);
          q1 = sumByIndices(months, periodIndices.q1);
          q2 = sumByIndices(months, periodIndices.q2);
          q3 = sumByIndices(months, periodIndices.q3);
          q4 = sumByIndices(months, periodIndices.q4);
          h1 = sumByIndices(months, periodIndices.h1);
          h2 = sumByIndices(months, periodIndices.h2);
        } else if (sortNo === 4) { // evol2026
          ttl = ratioByIndices(fcstMonths, actualMonths, periodIndices.ttl);
          ytdValue = row.ytd_amt ?? ratioByIndices(fcstMonths, actualMonths, ytd);
          ytgValue = ratioByIndices(fcstMonths, actualMonths, ytg);
          q1 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q1);
          q2 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q2);
          q3 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q3);
          q4 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q4);
          h1 = ratioByIndices(fcstMonths, actualMonths, periodIndices.h1);
          h2 = ratioByIndices(fcstMonths, actualMonths, periodIndices.h2);
        } else if (sortNo === 7) { // diff
          months = backendDiffMonths;
          ttl = row.ttl_amt ?? sumNullable(backendDiffMonths);
          ytdValue = row.ytd_amt ?? sumByIndices(backendDiffMonths, ytd);
          ytgValue = sumByIndices(backendDiffMonths, ytg);
          q1 = sumByIndices(backendDiffMonths, periodIndices.q1);
          q2 = sumByIndices(backendDiffMonths, periodIndices.q2);
          q3 = sumByIndices(backendDiffMonths, periodIndices.q3);
          q4 = sumByIndices(backendDiffMonths, periodIndices.q4);
          h1 = sumByIndices(backendDiffMonths, periodIndices.h1);
          h2 = sumByIndices(backendDiffMonths, periodIndices.h2);
        }

        return {
          key: `${row.group_id}-${row.sort_no}`,
          groupKey: row.group_id,
          rowSpan: index === 0 ? visibleRows.length : 0,
          metricKey: sortNo,
          metricLabel: row.metric_name,
          format,
          brand: row.brand_name ?? '',
          spu: getDisplaySpu(row),
          sku: row.goods_no ?? '',
          skuName: row.goods_name ?? '',
          goodsProperty: row.goods_property ?? '',
          category: row.catagory_name_lv3 ? displayFilterValue(row.catagory_name_lv3) : '',
          isAbnormal,
          abnormalReason,
          skuAbnormalFlag: row.sku_abnormal_flag ?? null,
          m1: months[0] ?? null,
          m2: months[1] ?? null,
          m3: months[2] ?? null,
          m4: months[3] ?? null,
          m5: months[4] ?? null,
          m6: months[5] ?? null,
          m7: months[6] ?? null,
          m8: months[7] ?? null,
          m9: months[8] ?? null,
          m10: months[9] ?? null,
          m11: months[10] ?? null,
          m12: months[11] ?? null,
          w1: months[0] ?? null,
          w2: months[1] ?? null,
          w3: months[2] ?? null,
          w4: months[3] ?? null,
          w5: months[4] ?? null,
          w6: months[5] ?? null,
          w7: months[6] ?? null,
          ttl,
          ytd: ytdValue,
          ytg: ytgValue,
          q1,
          q2,
          q3,
          q4,
          h1,
          h2,
          ttlShare: row.ttl_share ?? null,
          ytdShare: row.ytd_share ?? null,
          ytgShare: null,
          q1Share: null,
          q2Share: null,
          q3Share: null,
          q4Share: null,
        } satisfies DisplayRow;
      });
    });
};

const calculateBrandTotalRows = (
  rawRows: SpuAmountReportRow[],
  bizMonth: Dayjs,
  monthViewMode: MonthViewMode,
  visibleMetrics?: Set<MetricKey>,
): DisplayRow[] => {
  const { ytd, ytg } = getBizMonthIndices(bizMonth);
  const getSeries = monthViewMode === 'rolling' ? getRollingMonthSeries : getMonthSeries;
  
  const metricRows = new Map<number, SpuAmountReportRow[]>();
  rawRows.forEach((row) => {
    const current = metricRows.get(row.sort_no) ?? [];
    current.push(row);
    metricRows.set(row.sort_no, current);
  });

  const result: DisplayRow[] = [];
  const visibleMetricCount = Array.from(metricRows.keys()).filter((sortNo) =>
    !visibleMetrics || visibleMetrics.has(sortNo),
  ).length;
  
  metricRows.forEach((rows, sortNo) => {
    if (visibleMetrics && !visibleMetrics.has(sortNo)) {
      return;
    }

    const format = metricFormatBySortNo[sortNo] ?? 'number';
    const firstRow = rows[0];
    
    let months = MONTHS.map((_, index) => {
      return rows.reduce((acc, row) => acc + (getSeries(row)[index] ?? 0), 0);
    });
    const rowMap = new Map(metricRows);
    const actualMonths = MONTHS.map((_, index) =>
      (rowMap.get(1) ?? []).reduce((acc, row) => acc + (getSeries(row)[index] ?? 0), 0),
    );
    const fcstMonths = MONTHS.map((_, index) =>
      (rowMap.get(3) ?? []).reduce((acc, row) => acc + (getSeries(row)[index] ?? 0), 0),
    );
    const budgetMonths = MONTHS.map((_, index) =>
      (rowMap.get(6) ?? []).reduce((acc, row) => acc + (getSeries(row)[index] ?? 0), 0),
    );

    if (sortNo === 2) {
      months = aggregateMomSeries(rows, rowMap.get(1) ?? [], getSeries);
    } else if (sortNo === 4) {
      months = diffRateSeries(fcstMonths, actualMonths);
    } else if (sortNo === 5) {
      months = aggregateMomSeries(rows, rowMap.get(3) ?? [], getSeries);
    } else if (sortNo === 7) {
      months = diffSeries(fcstMonths, budgetMonths);
    }

    let ttl = null;
    let ytdValue = null;
    let ytgValue = null;
    let q1 = null;
    let q2 = null;
    let q3 = null;
    let q4 = null;
    let h1 = null;
    let h2 = null;

    if (monthViewMode !== 'rolling') {
      if (SUMMARY_METRIC_SORT_NOS.has(sortNo)) {
        ttl = sumNullable(months);
        ytdValue = sumByIndices(months, ytd);
        ytgValue = sumByIndices(months, ytg);
        q1 = sumByIndices(months, periodIndices.q1);
        q2 = sumByIndices(months, periodIndices.q2);
        q3 = sumByIndices(months, periodIndices.q3);
        q4 = sumByIndices(months, periodIndices.q4);
        h1 = sumByIndices(months, periodIndices.h1);
        h2 = sumByIndices(months, periodIndices.h2);
      } else if (sortNo === 4) {
        ttl = ratioByIndices(fcstMonths, actualMonths, periodIndices.ttl);
        ytdValue = ratioByIndices(fcstMonths, actualMonths, ytd);
        ytgValue = ratioByIndices(fcstMonths, actualMonths, ytg);
        q1 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q1);
        q2 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q2);
        q3 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q3);
        q4 = ratioByIndices(fcstMonths, actualMonths, periodIndices.q4);
        h1 = ratioByIndices(fcstMonths, actualMonths, periodIndices.h1);
        h2 = ratioByIndices(fcstMonths, actualMonths, periodIndices.h2);
      } else if (sortNo === 7) {
        ttl = sumNullable(months);
        ytdValue = sumByIndices(months, ytd);
        ytgValue = sumByIndices(months, ytg);
        q1 = sumByIndices(months, periodIndices.q1);
        q2 = sumByIndices(months, periodIndices.q2);
        q3 = sumByIndices(months, periodIndices.q3);
        q4 = sumByIndices(months, periodIndices.q4);
        h1 = sumByIndices(months, periodIndices.h1);
        h2 = sumByIndices(months, periodIndices.h2);
      }
    }

    result.push({
      key: `TOTAL-BRAND-${sortNo}`,
      groupKey: 'TOTAL-BRAND',
      rowSpan: result.length === 0 ? visibleMetricCount : 0,
      metricKey: sortNo,
      metricLabel: firstRow?.metric_name ?? '',
      format,
      brand: '所有品牌',
      spu: '',
      sku: '',
      skuName: '',
      goodsProperty: '',
      category: '',
      isAbnormal: false,
      abnormalReason: '',
      skuAbnormalFlag: null,
      m1: months[0] ?? null,
      m2: months[1] ?? null,
      m3: months[2] ?? null,
      m4: months[3] ?? null,
      m5: months[4] ?? null,
      m6: months[5] ?? null,
      m7: months[6] ?? null,
      m8: months[7] ?? null,
      m9: months[8] ?? null,
      m10: months[9] ?? null,
      m11: months[10] ?? null,
      m12: months[11] ?? null,
      w1: months[0] ?? null,
      w2: months[1] ?? null,
      w3: months[2] ?? null,
      w4: months[3] ?? null,
      w5: months[4] ?? null,
      w6: months[5] ?? null,
      w7: months[6] ?? null,
      ttl,
      ytd: ytdValue,
      ytg: ytgValue,
      q1,
      q2,
      q3,
      q4,
      h1,
      h2,
      ttlShare: null,
      ytdShare: null,
      ytgShare: null,
      q1Share: null,
      q2Share: null,
      q3Share: null,
      q4Share: null,
    });
  });

  // 按 sort_no 排序
  return result.sort((a, b) => a.metricKey - b.metricKey);
};

const paginateDisplayRowsByGroup = (rows: DisplayRow[], pageNo: number, pageSize: number) => {
  const groupedRows = new Map<string, DisplayRow[]>();
  rows.forEach((row) => {
    const current = groupedRows.get(row.groupKey) ?? [];
    current.push(row);
    groupedRows.set(row.groupKey, current);
  });

  const groups = Array.from(groupedRows.values());
  const startIndex = Math.max(0, (pageNo - 1) * pageSize);
  return groups.slice(startIndex, startIndex + pageSize).flat();
};

const dimensionColumnRender =
  (
    renderValueFn?: (value: string | boolean | number | null, row: DisplayRow) => React.ReactNode,
  ) =>
  (value: string | boolean | number | null, row: DisplayRow) => ({
    children: renderValueFn ? renderValueFn(value, row) : value,
    props: { rowSpan: row.rowSpan },
  });

const FinancialSpuBudgetComparison: React.FC = () => {
  const { toast } = useToast();
  const [groupBy, setGroupBy] = useState<GroupBy>('spu');
  const [bizMonth, setBizMonth] = useState<Dayjs>(() => getDefaultBizMonth());
  const [monthViewMode, setMonthViewMode] = useState<MonthViewMode>('year');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSpus, setSelectedSpus] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [budgetAbnormalFilter, setBudgetAbnormalFilter] = useState<BudgetAbnormalFilter>('all');
  const [skuAbnormalFilter, setSkuAbnormalFilter] = useState<SkuAbnormalFilter>('all');
  const [selectedProductAttrs, setSelectedProductAttrs] = useState<string[]>([]);
  const [specKeyword, setSpecKeyword] = useState('');
  const [skuDimsCollapsed, setSkuDimsCollapsed] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [groupPageNo, setGroupPageNo] = useState(1);
  const [groupPageSize, setGroupPageSize] = useState(30);
  const [visibleMetricKeysByGroup, setVisibleMetricKeysByGroup] = useState<Record<GroupBy, Set<MetricKey> | null>>({
    brand: null,
    category: null,
    spu: null,
    sku: null,
  });

  const visibleMetricKeys = useMemo(() => visibleMetricKeysByGroup[groupBy], [visibleMetricKeysByGroup, groupBy]);

  const maxSelectableBizMonth = useMemo(() => getDefaultBizMonth(), []);
  const bizDate = useMemo(() => bizMonth.startOf('month').format('YYYY-MM-DD'), [bizMonth]);
  const rollingMonthColumns = useMemo(() => buildRollingMonthColumns(bizDate), [bizDate]);

  useEffect(() => {
    if (bizMonth.isAfter(maxSelectableBizMonth, 'month')) {
      setBizMonth(maxSelectableBizMonth);
    }
  }, [bizMonth, maxSelectableBizMonth]);

  const allColumns = useMemo(
    () => [
      { key: 'brand', title: '品牌', group: 'dim' as const },
      { key: 'category', title: '三级分类', group: 'dim' as const },
      { key: 'spu', title: 'SPU', group: 'dim' as const },
      { key: 'sku', title: 'SKU', group: 'dim' as const },
      { key: 'skuName', title: 'SKU名称', group: 'dim' as const },
      { key: 'goodsProperty', title: '商品属性', group: 'dim' as const },
      { key: 'metricLabel', title: '指标', group: 'dim' as const },
      ...(monthViewMode === 'year'
        ? MONTHS.map((month) => ({ key: monthKey(month), title: `${month}月`, group: 'month' as const }))
        : rollingMonthColumns.map((month) => ({ key: month.key, title: month.title, group: 'month' as const }))),
      { key: 'ttl', title: 'TTL', group: 'sum' as const },
      { key: 'ytd', title: 'YTD', group: 'sum' as const },
      { key: 'ytg', title: 'YTG', group: 'sum' as const },
      { key: 'q1', title: 'Q1', group: 'sum' as const },
      { key: 'q2', title: 'Q2', group: 'sum' as const },
      { key: 'q3', title: 'Q3', group: 'sum' as const },
      { key: 'q4', title: 'Q4', group: 'sum' as const },
      { key: 'h1', title: 'H1', group: 'sum' as const },
      { key: 'h2', title: 'H2', group: 'sum' as const },
      { key: 'ttlShare', title: 'TTL占比', group: 'share' as const },
      { key: 'ytdShare', title: 'YTD占比', group: 'share' as const },
      { key: 'ytgShare', title: 'YTG占比', group: 'share' as const },
      { key: 'q1Share', title: 'Q1占比', group: 'share' as const },
      { key: 'q2Share', title: 'Q2占比', group: 'share' as const },
      { key: 'q3Share', title: 'Q3占比', group: 'share' as const },
      { key: 'q4Share', title: 'Q4占比', group: 'share' as const },
      { key: 'isAbnormal', title: '异常', group: 'anomaly' as const },
      { key: 'abnormalReason', title: '异常原因', group: 'anomaly' as const },
      { key: 'skuAbnormalFlag', title: 'SKU异常标志', group: 'anomaly' as const },
    ],
    [monthViewMode, rollingMonthColumns],
  );

  const defaultVisible = useMemo(() => {
    const next = new Set<string>([
      'brand',
      'category',
      'spu',
      'metricLabel',
      'isAbnormal',
      'abnormalReason',
      ...(monthViewMode === 'year' ? MONTHS.map((month) => monthKey(month)) : rollingMonthColumns.map((month) => month.key)),
    ]);

    if (groupBy === 'sku') {
      next.add('sku');
      next.add('skuName');
      next.add('goodsProperty');
      next.add('skuAbnormalFlag');
    }

    return next;
  }, [groupBy, monthViewMode, rollingMonthColumns]);

  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(defaultVisible);

  useEffect(() => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (monthViewMode === 'year') {
        ROLLING_MONTH_KEYS.forEach((key) => next.delete(key));
        MONTHS.forEach((month) => next.add(monthKey(month)));
      } else {
        MONTHS.forEach((month) => next.delete(monthKey(month)));
        rollingMonthColumns.forEach((month) => next.add(month.key));
      }
      if (groupBy === 'sku') {
        next.add('sku');
        next.add('skuName');
        next.add('goodsProperty');
        next.add('skuAbnormalFlag');
      } else {
        next.delete('sku');
        next.delete('skuName');
        next.delete('goodsProperty');
        next.delete('skuAbnormalFlag');
      }
      return next;
    });
  }, [groupBy, monthViewMode, rollingMonthColumns]);

  useEffect(() => {
    setGroupPageNo(1);
  }, [
    groupBy,
    bizDate,
    monthViewMode,
    selectedBrands,
    selectedCategories,
    selectedSpus,
    keyword,
    budgetAbnormalFilter,
    skuAbnormalFilter,
    selectedProductAttrs,
    specKeyword,
  ]);

  useEffect(() => {
    if (groupBy !== 'sku') {
      setSkuAbnormalFilter('all');
      setSelectedProductAttrs([]);
      setSpecKeyword('');
    }
    setSkuDimsCollapsed(true);
  }, [groupBy]);

  const { data: brandFilterData, isLoading: isBrandFilterLoading, refetch: refetchBrandFilters } = useQuery({
    queryKey: ['spuAmountBrandFilters'],
    queryFn: () => spuAmountApi.getFilterOptions(),
  });

  const { data: categoryFilterData, isLoading: isCategoryFilterLoading, refetch: refetchCategoryFilters } = useQuery({
    queryKey: ['spuAmountCategoryFilters', selectedBrands.join(',')],
    queryFn: () => spuAmountApi.getFilterOptions(selectedBrands.join(',')),
  });

  const { data: spuFilterData, isLoading: isSpuFilterLoading, refetch: refetchSpuFilters } = useQuery({
    queryKey: ['spuAmountSpuFilters', selectedBrands.join(','), selectedCategories.join(',')],
    queryFn: () => spuAmountApi.getFilterOptions(selectedBrands.join(','), selectedCategories.join(',')),
  });

  const {
    data: reportData,
    isLoading: isReportLoading,
    isFetching: isReportFetching,
    refetch: refetchReport,
  } = useQuery({
    queryKey: [
      'spuAmountReport',
      bizDate,
      groupBy,
      groupPageNo,
      groupPageSize,
      monthViewMode,
      selectedBrands.join(','),
      selectedCategories.join(','),
      selectedSpus.join(','),
      keyword.trim(),
      budgetAbnormalFilter,
      skuAbnormalFilter,
      selectedProductAttrs.join(','),
      specKeyword.trim(),
    ],
    queryFn: () =>
      spuAmountApi.getReport({
        bizDate,
        groupPageNo,
        groupPageSize,
        groupLevel: groupBy,
        monthViewMode,
        brandName: selectedBrands.join(','),
        categoryNameLv3: selectedCategories.join(','),
        spuNo: selectedSpus.join(','),
        goodsName: keyword.trim(),
        budgetAbnormal: budgetAbnormalFilter,
        skuAbnormal: groupBy === 'sku' ? skuAbnormalFilter : 'all',
        productAttrName: groupBy === 'sku' ? selectedProductAttrs.join(',') : '',
        specName: groupBy === 'sku' ? specKeyword.trim() : '',
      }),
  });

  const rawTotalGroups = useMemo(() => reportData?.res_content?.data_list?.[0]?.total_groups ?? 0, [reportData]);
  const needsFullDataset = false;

  const {
    data: allReportData,
    isLoading: isAllReportLoading,
    isFetching: isAllReportFetching,
    refetch: refetchAllReport,
  } = useQuery({
    queryKey: [
      'spuAmountReportAll',
      bizDate,
      groupBy,
      rawTotalGroups,
      monthViewMode,
      selectedBrands.join(','),
      selectedCategories.join(','),
      selectedSpus.join(','),
      keyword.trim(),
      budgetAbnormalFilter,
      skuAbnormalFilter,
      selectedProductAttrs.join(','),
      specKeyword.trim(),
    ],
    enabled: needsFullDataset && rawTotalGroups > 0,
    queryFn: () =>
      spuAmountApi.getReport({
        bizDate,
        groupPageNo: 1,
        groupPageSize: rawTotalGroups,
        groupLevel: groupBy,
        monthViewMode,
        brandName: selectedBrands.join(','),
        categoryNameLv3: selectedCategories.join(','),
        spuNo: selectedSpus.join(','),
        goodsName: keyword.trim(),
        budgetAbnormal: budgetAbnormalFilter,
        skuAbnormal: groupBy === 'sku' ? skuAbnormalFilter : 'all',
        productAttrName: groupBy === 'sku' ? selectedProductAttrs.join(',') : '',
        specName: groupBy === 'sku' ? specKeyword.trim() : '',
      }),
  });

  const brandFilterRecords = useMemo<SpuAmountFilterItem[]>(() => brandFilterData?.res_content?.data_list ?? [], [brandFilterData]);
  const categoryFilterRecords = useMemo<SpuAmountFilterItem[]>(() => categoryFilterData?.res_content?.data_list ?? [], [categoryFilterData]);
  const spuFilterRecords = useMemo<SpuAmountFilterItem[]>(() => spuFilterData?.res_content?.data_list ?? [], [spuFilterData]);
  const reportRows = useMemo<SpuAmountReportRow[]>(() => reportData?.res_content?.data_list ?? [], [reportData]);
  const allReportRows = useMemo<SpuAmountReportRow[]>(() => allReportData?.res_content?.data_list ?? [], [allReportData]);

  // 从接口数据中动态提取指标列表（使用当前汇总粒度的数据）
  const allMetricOptions = useMemo(() => {
    const metricMap = new Map<number, string>();
    reportRows.forEach((row) => {
      if (!metricMap.has(row.sort_no)) {
        metricMap.set(row.sort_no, row.metric_name);
      }
    });
    return Array.from(metricMap.entries())
      .map(([sortNo, name]) => ({ sortNo, name }))
      .sort((a, b) => a.sortNo - b.sortNo);
  }, [reportRows]);
  const selectedMetricKeys = useMemo(
    () => visibleMetricKeys ? Array.from(visibleMetricKeys) : allMetricOptions.map((metric) => metric.sortNo),
    [allMetricOptions, visibleMetricKeys],
  );

  const snapshotRefreshTime = useMemo(() => {
    const timestamps = reportRows
      .map((row) => row.refresh_time)
      .filter((value): value is string => Boolean(value));
    if (!timestamps.length) return '';
    const latest = timestamps.sort()[timestamps.length - 1];
    return dayjs(latest).format('YYYY-MM-DD HH:mm');
  }, [reportRows]);

  const brandOptions = useMemo(() => buildBrandOptions(brandFilterRecords), [brandFilterRecords]);
  const categoryOptions = useMemo(() => buildCategoryOptions(categoryFilterRecords), [categoryFilterRecords]);
  const spuOptions = useMemo(() => buildSpuOptions(spuFilterRecords), [spuFilterRecords]);

  useEffect(() => {
    setSelectedCategories((prev) => sanitizeSelectedValues(prev, categoryOptions.map((item) => item.value)));
  }, [categoryOptions]);

  useEffect(() => {
    setSelectedSpus((prev) => sanitizeSelectedValues(prev, spuOptions.map((item) => item.value)));
  }, [spuOptions]);

  const pageTableData = useMemo(() => {
    const displayRows = buildDisplayRows(reportRows, bizMonth, monthViewMode, 'all', 'all', groupBy, visibleMetricKeys);
    if (groupBy === 'brand') {
      const brandTotalRows = calculateBrandTotalRows(reportRows, bizMonth, monthViewMode, visibleMetricKeys);
      return [...brandTotalRows, ...displayRows];
    }
    return displayRows;
  }, [bizMonth, groupBy, monthViewMode, reportRows, visibleMetricKeys]);
  
  const filteredAllTableData = useMemo(() => {
    const displayRows = buildDisplayRows(allReportRows, bizMonth, monthViewMode, budgetAbnormalFilter, groupBy === 'sku' ? skuAbnormalFilter : 'all', groupBy, visibleMetricKeys);
    if (groupBy === 'brand') {
      const brandTotalRows = calculateBrandTotalRows(allReportRows, bizMonth, monthViewMode, visibleMetricKeys);
      return [...brandTotalRows, ...displayRows];
    }
    return displayRows;
  }, [allReportRows, bizMonth, budgetAbnormalFilter, groupBy, monthViewMode, skuAbnormalFilter, visibleMetricKeys]);
  const filteredTotalGroups = useMemo(
    () => new Set(filteredAllTableData.map((row) => row.groupKey)).size,
    [filteredAllTableData],
  );
  const tableData = useMemo(
    () => (needsFullDataset ? paginateDisplayRowsByGroup(filteredAllTableData, groupPageNo, groupPageSize) : pageTableData),
    [filteredAllTableData, groupPageNo, groupPageSize, needsFullDataset, pageTableData],
  );
  const totalGroups = needsFullDataset ? filteredTotalGroups : rawTotalGroups;
  const isLoading =
    isBrandFilterLoading ||
    isCategoryFilterLoading ||
    isSpuFilterLoading ||
    isReportLoading ||
    (needsFullDataset && isAllReportLoading);
  const isTableFetching = isReportFetching || (needsFullDataset && isAllReportFetching);

  const columns = useMemo(() => {
    const SKU_ABNORMAL_THRESHOLD = 0.15;

    const valueColumn = (
      key: keyof DisplayRow,
      title: string,
      options?: { displayFmt?: ValueFormat; exportFmt?: XlsxCellFormat | 'text' },
    ) => ({
      title,
      dataIndex: key,
      key,
      width: 120,
      align: 'right' as const,
      exportFmt: options?.exportFmt ?? 'money',
      render: (value: number | null, row: DisplayRow) => {
        const text = renderValue(value, options?.displayFmt ?? row.format);
        // SKU异常指标的 sort_no 需要根据实际数据确定，这里假设为 13
        if (row.metricKey === 13 && value !== null && Math.abs(value - 1) > SKU_ABNORMAL_THRESHOLD) {
          return <span style={{ color: '#f5222d' }}>{text}</span>;
        }
        return text;
      },
    });

    const textColumn = (key: keyof DisplayRow, title: string, fixed?: 'left', width = 140, merge = false) => {
      const keyStr = String(key);
      const wrapKeys = ['brand', 'category', 'spu', 'skuName'];
      const noWrapKeys = ['sku', 'metricLabel'];
      const shouldWrap = wrapKeys.includes(keyStr);
      const shouldEllipsis = noWrapKeys.includes(keyStr);
      
      return {
        title,
        dataIndex: key,
        key,
        fixed,
        width,
        ellipsis: shouldEllipsis,
        whiteSpace: shouldWrap ? 'normal' : 'nowrap',
        wordBreak: shouldWrap ? 'break-word' : 'keep-all',
        exportFmt: 'text' as const,
        render: merge ? dimensionColumnRender() : undefined,
      };
    };

    const isSkuDimsCollapsed = groupBy === 'sku' && skuDimsCollapsed;
    const visible = (key: string) => visibleKeys.has(key) && !(isSkuDimsCollapsed && SKU_COLLAPSIBLE_DIM_KEYS.has(key));

    const dims = [
      { ...textColumn('brand', '品牌', 'left', 80, true) },
      { ...textColumn('category', '三级分类', 'left', 100, true) },
      ...(groupBy === 'spu' || groupBy === 'sku' ? [{ ...textColumn('spu', 'SPU', 'left', 120, true) }] : []),
      ...(groupBy === 'sku'
        ? [
            {
              ...textColumn('sku', 'SKU', 'left', 100, true),
              exportTitle: 'SKU',
              title: (
                <Space size={6}>
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setSkuDimsCollapsed((prev) => !prev)}>
                    {skuDimsCollapsed ? '展开' : '缩进'}
                  </Button>
                  <span>SKU</span>
                </Space>
              ),
            },
            { ...textColumn('skuName', 'SKU名称', undefined, 140, true) },
            {
              ...textColumn('goodsProperty', '商品属性', undefined, 160, true),
              render: dimensionColumnRender((value) => (
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {String(value ?? '').split(/\n|\r\n/).map((item, index) => {
                    const processedItem = item.replace(
                      /(上市日期：\d{4}-\d{2}-\d{2}) \d{2}:\d{2}:\d{2}/g,
                      '$1'
                    );
                    return <div key={index}>{processedItem}</div>;
                  })}
                </div>
              )),
            },
          ]
        : []),
      {
        title: '指标',
        dataIndex: 'metricLabel',
        key: 'metricLabel',
        width: 140,
        fixed: 'left',
        ellipsis: true,
        whiteSpace: 'nowrap',
        exportFmt: 'text' as const,
        render: (value: string, row: DisplayRow) => (
          <span style={{ fontWeight: row.metricKey === FCST_SORT_NO || row.metricKey === DIFF_SORT_NO ? 600 : 500 }}>{value}</span>
        ),
      },
    ].filter((column) => visible(String(column.key)));

    const monthCols = (monthViewMode === 'year'
      ? MONTHS.map((month) => valueColumn(monthKey(month) as keyof DisplayRow, `${month}月`))
      : rollingMonthColumns.map((month) => valueColumn(month.key as keyof DisplayRow, month.title))
    ).filter((column) => visible(String(column.key)));
    const summaryCols = (getVisibleSummaryKeys(monthViewMode) as Array<keyof DisplayRow>)
      .map((key) => valueColumn(key, String(key).toUpperCase()))
      .filter((column) => visible(String(column.key)));
    const shareCols = monthViewMode === 'year'
      ? (['ttlShare', 'ytdShare', 'ytgShare', 'q1Share', 'q2Share', 'q3Share', 'q4Share'] as Array<keyof DisplayRow>)
          .map((key) => valueColumn(key, String(key).replace('Share', '占比').toUpperCase(), { displayFmt: 'percent', exportFmt: 'percent' }))
          .filter((column) => visible(String(column.key)))
      : [];
    const anomalyCols = [
      {
        title: '异常',
        dataIndex: 'isAbnormal',
        key: 'isAbnormal',
        width: 96,
        align: 'center' as const,
        exportFmt: 'text' as const,
        render: dimensionColumnRender((value) => (value ? <Tag color="red">异常</Tag> : <Tag>正常</Tag>)),
      },
      {
        title: '异常原因',
        dataIndex: 'abnormalReason',
        key: 'abnormalReason',
        width: 180,
        ellipsis: true,
        exportFmt: 'text' as const,
        render: dimensionColumnRender(),
      },
      ...(groupBy === 'sku'
        ? [
            {
              title: 'SKU异常标志',
              dataIndex: 'skuAbnormalFlag',
              key: 'skuAbnormalFlag',
              width: 120,
              align: 'center' as const,
              exportFmt: 'text' as const,
              render: dimensionColumnRender((value) => (Number(value) === 1 ? <Tag color="red">异常</Tag> : <Tag>正常</Tag>)),
            },
          ]
        : []),
    ].filter((column) => visible(String(column.key)));

    return [
      ...dims,
      ...(monthCols.length ? [{ title: '月份', key: 'months', children: monthCols }] : []),
      ...(summaryCols.length ? [{ title: '汇总', key: 'summary', children: summaryCols }] : []),
      ...(shareCols.length ? [{ title: '占比', key: 'share', children: shareCols }] : []),
      ...anomalyCols,
    ];
  }, [groupBy, monthViewMode, rollingMonthColumns, skuDimsCollapsed, visibleKeys]);

  const columnGroups = useMemo(() => {
    const orderedGroups: Array<{ key: string; label: string }> = [
      { key: 'dim', label: '维度列' },
      { key: 'month', label: '月份' },
      { key: 'sum', label: '汇总' },
      { key: 'share', label: '占比' },
      { key: 'anomaly', label: '异常' },
    ];
    return orderedGroups
      .filter((group) => monthViewMode === 'year' || (group.key !== 'sum' && group.key !== 'share'))
      .map((group) => ({
      key: group.key,
      label: group.label,
      items: allColumns.filter((item) => item.group === group.key),
    }));
  }, [allColumns, monthViewMode]);

  const handleVisibleGroupChange = (groupKey: string, list: string[]) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      const groupItems = columnGroups.find((group) => group.key === groupKey)?.items ?? [];
      groupItems.forEach((item) => next.delete(item.key));
      list.forEach((key) => next.add(key));
      return next;
    });
  };

  const handleRefresh = async () => {
    await Promise.all([
      refetchReport(),
      refetchBrandFilters(),
      refetchCategoryFilters(),
      refetchSpuFilters(),
      ...(needsFullDataset ? [refetchAllReport()] : []),
    ]);
    toast({ title: '刷新成功', description: '数据已更新' });
  };

  const getExportRows = (rows: DisplayRow[], flat: ReturnType<typeof flattenColumns>) =>
    rows.map((row) =>
      flat.map((column) => {
        const key = column.dataIndex as keyof DisplayRow | undefined;
        if (!key) return null;
        const value = row[key];
        if (key === 'skuAbnormalFlag') return value === 1 ? '异常' : '正常';
        if (typeof value === 'boolean') return value ? '异常' : '正常';
        if (typeof value === 'number') return String(renderValue(value, row.format));
        if (value === null) return '';
        return value as string;
      }),
    );

  const handleExport = async () => {
    if (!totalGroups) {
      toast({ title: '导出失败', description: '暂无可导出的数据', variant: 'destructive' });
      return;
    }

    setIsExporting(true);
    toast({ title: '正在导出', description: '正在按当前筛选条件查询全部数据...' });

    try {
      const exportResponse = await spuAmountApi.getReport({
        bizDate,
        groupPageNo: 1,
        groupPageSize: totalGroups,
        groupLevel: groupBy,
        monthViewMode,
        brandName: selectedBrands.join(','),
        categoryNameLv3: selectedCategories.join(','),
        spuNo: selectedSpus.join(','),
        goodsName: keyword.trim(),
        budgetAbnormal: budgetAbnormalFilter,
        skuAbnormal: groupBy === 'sku' ? skuAbnormalFilter : 'all',
        productAttrName: groupBy === 'sku' ? selectedProductAttrs.join(',') : '',
        specName: groupBy === 'sku' ? specKeyword.trim() : '',
      });
      const exportRowsRaw = exportResponse.res_content?.data_list ?? [];
      const exportTableData = buildDisplayRows(exportRowsRaw, bizMonth, monthViewMode, 'all', 'all', groupBy, visibleMetricKeys ?? undefined);

      if (!exportTableData.length) {
        toast({ title: '导出失败', description: '暂无可导出的数据', variant: 'destructive' });
        return;
      }

      let flat = flattenColumns(columns as any[]);
      if (groupBy === 'sku') {
        const requiredSkuDimColumns: ReturnType<typeof flattenColumns> = [
          { title: '品牌', key: 'brand', dataIndex: 'brand', exportFmt: 'text' },
          { title: '三级分类', key: 'category', dataIndex: 'category', exportFmt: 'text' },
          { title: 'SPU', key: 'spu', dataIndex: 'spu', exportFmt: 'text' },
        ];
        const requiredSkuDimKeys = new Set(requiredSkuDimColumns.map((column) => column.key));
        flat = [
          ...requiredSkuDimColumns,
          ...flat.filter((column) => !requiredSkuDimKeys.has(column.key)),
        ];
      }
      const headers = flat.map((item) => item.title);
      const formats = flat.map(() => 'text' as const);
      const rows = getExportRows(exportTableData, flat);

      downloadXlsx({
        sheetName: `FCST与预算比较-${groupBy}`,
        headers,
        rows,
        formats,
        fileName: `FCST与预算比较-${groupBy}-${bizMonth.format('YYYY-MM')}.xlsx`,
      });
      toast({ title: '导出完成', description: '已生成并触发下载（xlsx）' });
    } catch (error) {
      console.error('Export failed', error);
      toast({ title: '导出失败', description: '查询或生成 Excel 时发生错误', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Title level={3} className="tech-page-title" style={{ margin: 0, fontSize: 22 }}>
          FCST与预算比较（发货维度）
        </Title>
        <Space>
          {snapshotRefreshTime ? (
            <Text type="secondary">数据刷新：{snapshotRefreshTime}</Text>
          ) : null}
          <Tooltip title={`列表中的“${bizMonth.year()}年FCST”按年月切分取值：${bizMonth.format('M')}月之前的月份使用实际销售金额，${bizMonth.format('M')}月及之后的月份使用FCST预测金额。滚动窗口口径展示预测月前1个月实际和预测月起6个月预测。`}>
            <Text type="secondary" style={{ cursor: 'help' }}>
              <InfoCircleOutlined style={{ marginRight: 6 }} />
              口径说明
            </Text>
          </Tooltip>
          <Button icon={<SettingOutlined />} onClick={() => setDrawerOpen(true)}>
            显示设置
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={isTableFetching}>
            刷新
          </Button>
          <Button icon={<FileExcelOutlined />} onClick={handleExport} loading={isExporting}>
            导出
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={3}>
            <Text strong>汇总粒度：</Text>
            <Select value={groupBy} onChange={(value) => setGroupBy(value)} style={{ width: '100%', marginTop: 6 }}>
              <Option value="brand">品牌汇总</Option>
              <Option value="category">三级分类汇总</Option>
              <Option value="spu">SPU汇总</Option>
              <Option value="sku">SKU明细</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Text strong>年月：</Text>
            <DatePicker
              picker="month"
              locale={zhCN}
              value={bizMonth}
              format="YYYY年M月"
              inputReadOnly
              allowClear={false}
              style={{ width: '100%', marginTop: 6 }}
              disabledDate={(current) => Boolean(current && current.startOf('month').isAfter(maxSelectableBizMonth, 'month'))}
              onChange={(value) => {
                if (!value) return;
                const nextMonth = value.startOf('month');
                if (nextMonth.isAfter(maxSelectableBizMonth, 'month')) {
                  setBizMonth(maxSelectableBizMonth);
                  return;
                }
                setBizMonth(nextMonth);
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text strong>月份展示口径：</Text>
            <Select value={monthViewMode} onChange={setMonthViewMode} style={{ width: '100%', marginTop: 6 }}>
              <Option value="year">自然年</Option>
              <Option value="rolling">前1月实际+未来6月预测</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text strong>品牌：</Text>
            <Select
              mode="multiple"
              value={selectedBrands}
              onChange={setSelectedBrands}
              style={{ width: '100%', marginTop: 6 }}
              placeholder="全部"
              allowClear
              options={brandOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text strong>三级分类：</Text>
            <Select
              mode="multiple"
              showSearch
              value={selectedCategories}
              onChange={setSelectedCategories}
              style={{ width: '100%', marginTop: 6 }}
              placeholder="全部"
              allowClear
              options={categoryOptions}
              optionFilterProp="label"
              filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>SPU：</Text>
            <Select
              mode="multiple"
              showSearch
              value={selectedSpus}
              onChange={setSelectedSpus}
              style={{ width: '100%', marginTop: 6 }}
              placeholder="全部"
              allowClear
              options={spuOptions}
              optionFilterProp="label"
              filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>SKU：</Text>
            <Input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="输入商品编码或名称" style={{ marginTop: 6 }} allowClear />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text strong>产品属性：</Text>
            <Select
              mode="multiple"
              value={selectedProductAttrs}
              onChange={setSelectedProductAttrs}
              style={{ width: '100%', marginTop: 6 }}
              placeholder="全部"
              allowClear
              disabled={groupBy !== 'sku'}
              options={productAttrOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text strong>规格：</Text>
            <Input
              value={specKeyword}
              onChange={(event) => setSpecKeyword(event.target.value)}
              placeholder="输入规格名称"
              style={{ marginTop: 6 }}
              allowClear
              disabled={groupBy !== 'sku'}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text strong>预算异常：</Text>
            <Select
              value={budgetAbnormalFilter}
              onChange={setBudgetAbnormalFilter}
              style={{ width: '100%', marginTop: 6 }}
            >
              <Option value="all">全部</Option>
              <Option value="abnormal">异常</Option>
              <Option value="normal">正常</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text strong>SKU异常：</Text>
            <Select
              value={skuAbnormalFilter}
              onChange={setSkuAbnormalFilter}
              style={{ width: '100%', marginTop: 6 }}
              disabled={groupBy !== 'sku'}
            >
              <Option value="all">全部</Option>
              <Option value="abnormal">异常</Option>
              <Option value="normal">正常</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Card bodyStyle={{ padding: 12 }}>
        <Spin spinning={isLoading || isTableFetching}>
          {tableData.length ? (
            <>
              <Table
                rowKey="key"
                columns={columns as any}
                dataSource={tableData}
                size="middle"
                sticky
                pagination={false}
                scroll={{ x: 'max-content', y: 620 }}
                rowClassName={(row: DisplayRow) => {
                  const classes: string[] = [];
                  if (row.rowSpan > 0) classes.push('border-t-2 border-slate-500');
                  if (row.metricKey === FCST_SORT_NO || row.metricKey === DIFF_SORT_NO) classes.push('bg-slate-200/70');
                  if (row.metricKey === BUDGET_SORT_NO) classes.push('bg-blue-100/60');
                  return classes.join(' ');
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Pagination
                  current={groupPageNo}
                  pageSize={groupPageSize}
                  total={totalGroups}
                  showSizeChanger
                  showQuickJumper
                  pageSizeOptions={['10', '20', '30', '50']}
                  showTotal={(total) => `共 ${total} 组`}
                  onChange={(page, pageSize) => {
                    setGroupPageNo(page);
                    setGroupPageSize(pageSize);
                  }}
                />
              </div>
            </>
          ) : (
            <Empty description="暂无数据" style={{ padding: '48px 0' }} />
          )}
        </Spin>
      </Card>

      <Drawer title="显示设置" open={drawerOpen} onClose={() => setDrawerOpen(false)} width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {columnGroups.map((group) => (
            <Card key={group.key} size="small" title={group.label}>
              <Checkbox.Group
                style={{ width: '100%' }}
                value={group.items.map((item) => item.key).filter((key) => visibleKeys.has(key))}
                onChange={(list) => handleVisibleGroupChange(group.key, list as string[])}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {group.items.map((item) => (
                    <Checkbox key={item.key} value={item.key}>
                      {item.title}
                    </Checkbox>
                  ))}
                </div>
              </Checkbox.Group>
            </Card>
          ))}
          <Card key="metrics" size="small" title="指标行">
            <Checkbox.Group
              style={{ width: '100%' }}
              value={selectedMetricKeys}
              onChange={(list) => {
                setVisibleMetricKeysByGroup((prev) => ({
                  ...prev,
                  [groupBy]: new Set(list as MetricKey[]),
                }));
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {allMetricOptions.map((metric) => (
                  <Checkbox key={metric.sortNo} value={metric.sortNo}>
                    {metric.name}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </Card>
        </div>
      </Drawer>
    </div>
  );
};

export default FinancialSpuBudgetComparison;
